/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import { useCallback } from 'react';
import { isString } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import type { Login2FAModalData } from '../components/modals';
import { setAuthVar } from '../utils/apollo-vars';
import { setToken } from '../utils/auth';
import { setAuthIdentityId } from '../utils/auth-identity-id';
import { sendSentryError } from '../utils/sentry';
import type {
  SignInMutation,
  GoogleSignInMutation,
  GoogleSignUpMutation,
} from '../apollo';
import { useAlert, useModals, useReminder2FAContext } from '../providers';

export const useSignIn = () => {
  const { setAlert } = useAlert();
  const { t } = useTranslation();
  const { dispatch } = useModals();

  const { setShouldBeShown: setReminderShouldBeShown } =
    useReminder2FAContext();

  return useCallback(
    ({
      data: { token, totpEnabled, authIdentityID },
      config: incomeConfig,
    }: CallbackParams) => {
      if (!(isString(token) && !!token)) {
        setAlert({ type: 'error', message: t('LOGIN_FORM__errorText') });
        sendSentryError(new Error(`Token is not a string, or it's empty`));
        return;
      }

      setToken(token);
      setAuthIdentityId(authIdentityID);

      const config = { ...defConfig, ...incomeConfig };

      if (totpEnabled) {
        dispatch({
          type: 'setModalContent',
          payload: {
            name: 'login2FA',
            data: {
              socialProvider: config.socialProvider,
              rememberFlag: config.rememberFlag,
            },
          },
        });
        return;
      }

      if (config.with2faReminder) {
        setReminderShouldBeShown(true);
      }

      setAuthVar(true);
    },
    [t, setAlert, dispatch, setReminderShouldBeShown]
  );
};

type Config = Partial<Login2FAModalData> & {
  with2faReminder?: boolean;
};

const defConfig: Required<Config> = {
  with2faReminder: false,
  socialProvider: false,
  rememberFlag: false,
};

type CallbackParams = {
  data:
    | SignInMutation['signIn']
    | GoogleSignInMutation['googleSignIn']
    | GoogleSignUpMutation['googleSignUp'];
  config?: Config;
};
